import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Folder, Link2, MessageSquare, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FolderResults from './FolderResults';
import LinkResults from './LinkResults';
import MessageResults from './MessageResults';
import ResultsPanel from './ResultsPanel';

import { setActiveTab, setPage, setFoldersPage, setLinksPage, setMessagesPage } from '../../../../../redux/actions/search.action';
import {
    selectFolders,
    selectLinks,
    selectMessages,
    selectIsFetchingNewData,
    selectViewMode,
    selectActiveTab,
    selectPage,
    selectPageSize,
    selectFoldersPage,
    selectLinksPage,
    selectMessagesPage
} from '../../../../../redux/selectors/search.selectors';

const SearchResults = () => {
    const dispatch = useDispatch();

    // Selectors
    const folders = useSelector(selectFolders);
    const links = useSelector(selectLinks);
    const messages = useSelector(selectMessages);
    const isFetchingNewData = useSelector(selectIsFetchingNewData);
    const viewMode = useSelector(selectViewMode);
    const activeTab = useSelector(selectActiveTab);
    const page = useSelector(selectPage);
    const pageSize = useSelector(selectPageSize);
    const foldersPage = useSelector(selectFoldersPage);
    const linksPage = useSelector(selectLinksPage);
    const messagesPage = useSelector(selectMessagesPage);

    // Calculate paginated data and indices based on view mode
    let paginatedFolders, paginatedLinks, paginatedMessages;
    let foldersStartIndex, foldersEndIndex, linksStartIndex, linksEndIndex, messagesStartIndex, messagesEndIndex;

    if (viewMode === 'tabs') {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        paginatedFolders = activeTab === 0 ? folders.slice(startIndex, endIndex) : folders;
        paginatedLinks = activeTab === 1 ? links.slice(startIndex, endIndex) : links;
        paginatedMessages = activeTab === 2 ? messages.slice(startIndex, endIndex) : messages;

        foldersStartIndex = activeTab === 0 ? startIndex : 0;
        foldersEndIndex = activeTab === 0 ? Math.min(endIndex, folders.length) : folders.length;
        linksStartIndex = activeTab === 1 ? startIndex : 0;
        linksEndIndex = activeTab === 1 ? Math.min(endIndex, links.length) : links.length;
        messagesStartIndex = activeTab === 2 ? startIndex : 0;
        messagesEndIndex = activeTab === 2 ? Math.min(endIndex, messages.length) : messages.length;
    } else {
        foldersStartIndex = (foldersPage - 1) * pageSize;
        foldersEndIndex = Math.min(foldersStartIndex + pageSize, folders.length);
        paginatedFolders = folders.slice(foldersStartIndex, foldersEndIndex);

        linksStartIndex = (linksPage - 1) * pageSize;
        linksEndIndex = Math.min(linksStartIndex + pageSize, links.length);
        paginatedLinks = links.slice(linksStartIndex, linksEndIndex);

        messagesStartIndex = (messagesPage - 1) * pageSize;
        messagesEndIndex = Math.min(messagesStartIndex + pageSize, messages.length);
        paginatedMessages = messages.slice(messagesStartIndex, messagesEndIndex);
    }

    // Handlers
    const handleTabChange = (val) => dispatch(setActiveTab(parseInt(val)));
    const handlePageChange = (_, newPage) => dispatch(setPage(newPage));
    const handleFoldersPageChange = (_, newPage) => dispatch(setFoldersPage(newPage));
    const handleLinksPageChange = (_, newPage) => dispatch(setLinksPage(newPage));
    const handleMessagesPageChange = (_, newPage) => dispatch(setMessagesPage(newPage));

    // Tabs view shares one page counter; split view pages each panel independently
    const isTabs = viewMode === 'tabs';
    const panels = [
        {
            key: 'folders',
            results: FolderResults,
            folders: paginatedFolders,
            totalCount: folders.length,
            startIndex: foldersStartIndex,
            endIndex: foldersEndIndex,
            page: isTabs ? page : foldersPage,
            onPageChange: isTabs ? handlePageChange : handleFoldersPageChange,
        },
        {
            key: 'links',
            results: LinkResults,
            links: paginatedLinks,
            totalCount: links.length,
            startIndex: linksStartIndex,
            endIndex: linksEndIndex,
            page: isTabs ? page : linksPage,
            onPageChange: isTabs ? handlePageChange : handleLinksPageChange,
        },
        {
            key: 'messages',
            results: MessageResults,
            messages: paginatedMessages,
            totalCount: messages.length,
            startIndex: messagesStartIndex,
            endIndex: messagesEndIndex,
            page: isTabs ? page : messagesPage,
            onPageChange: isTabs ? handlePageChange : handleMessagesPageChange,
        },
    ];

    if (isFetchingNewData) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="text-xs text-muted-foreground">Searching...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 px-3 pb-2">
            {isTabs ? (
                <Tabs
                    value={activeTab.toString()}
                    onValueChange={handleTabChange}
                    className="h-full flex flex-col"
                >
                    <TabsList className="w-fit h-8 p-0.5 bg-muted/60 rounded-lg">
                        <TabsTrigger value="0" className="gap-1.5 px-3 text-xs h-7 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm">
                            <Folder className="h-3 w-3" />
                            Folders
                            <Badge variant="secondary" className="h-4 px-1 text-[9px] font-mono ml-0.5">
                                {folders.length}
                            </Badge>
                        </TabsTrigger>
                        <TabsTrigger value="1" className="gap-1.5 px-3 text-xs h-7 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm">
                            <Link2 className="h-3 w-3" />
                            Links
                            <Badge variant="secondary" className="h-4 px-1 text-[9px] font-mono ml-0.5 bg-primary/15 text-primary dark:bg-primary/25 dark:text-primary">
                                {links.length}
                            </Badge>
                        </TabsTrigger>
                        <TabsTrigger value="2" className="gap-1.5 px-3 text-xs h-7 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm">
                            <MessageSquare className="h-3 w-3" />
                            Messages
                            <Badge variant="secondary" className="h-4 px-1 text-[9px] font-mono ml-0.5 bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300">
                                {messages.length}
                            </Badge>
                        </TabsTrigger>
                    </TabsList>

                    {panels.map(({ key, ...panel }, index) => (
                        <TabsContent key={key} value={index.toString()} className="flex-1 min-h-0 mt-2">
                            <ResultsPanel accent={key} className="h-full" pageSize={pageSize} {...panel} />
                        </TabsContent>
                    ))}
                </Tabs>
            ) : (
                /* Split View */
                <div className="h-full grid grid-cols-3 gap-2.5">
                    {panels.map(({ key, ...panel }) => (
                        <ResultsPanel key={key} accent={key} pageSize={pageSize} {...panel} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchResults;
